import React, { useState, useRef, useEffect } from 'react';
import {
  MapContainer,
  TileLayer,
  Marker,
  Popup,
  Polygon,
  useMap,
} from 'react-leaflet';
import L from 'leaflet';
import { MapPin, MapPinHouse, X } from 'lucide-react';
import ReactDOMServer from 'react-dom/server';
import 'leaflet/dist/leaflet.css';
import { Input, Popover } from 'antd';
import Spinner from "@src/components/spinner";

// Load Google Maps script
const loadGoogleScript = (callback) => {
  if (window.google && window.google.maps) {
    callback();
    return;
  }

  const existingScript = document.getElementById('googleMapsScript');
  if (!existingScript) {
    const script = document.createElement('script');
    script.id = 'googleMapsScript';
    script.src = `https://maps.googleapis.com/maps/api/js?key=AIzaSyDTfTaiEKNa_6IKNneZ75T_6xvd1mCwXVQ&libraries=places`;
    

    script.async = true;
    script.defer = true;
    script.onload = callback;
    document.head.appendChild(script);
  } else {
    existingScript.onload = callback;
  }
};

// Custom Marker
const DraggableMarker = ({ position, onDragEnd }) => {
  const markerRef = useRef(null);
  const iconHTML = ReactDOMServer.renderToString(
    <svg xmlns="http://www.w3.org/2000/svg" width="48" height="52" viewBox="0 0 48 72" fill="none">
      <path d="M24 0C11.4 0 0 10.8 0 24C0 39.6 24 72 24 72C24 72 48 39.6 48 24C48 10.8 36.6 0 24 0Z" fill="#4285F4"/>
      <circle cx="24" cy="24" r="10" fill="none" stroke="white" strokeWidth="2"/>
    </svg>
  );
  const customIcon = L.divIcon({
    html: iconHTML,
    className: '',
    iconSize: [40, 40],
    iconAnchor: [20, 40],
    popupAnchor: [0, -40],
  });

  return (
    <Marker
      draggable
      ref={markerRef}
      position={position}
      icon={customIcon}
      eventHandlers={{
        dragend() {
          const marker = markerRef.current;
          if (marker) {
            const { lat, lng } = marker.getLatLng();
            onDragEnd([lat, lng]);
          }
        },
      }}
    >
      <Popup>Drag me to change location</Popup>
    </Marker>
  );
};

// Center map on position
const MapController = ({ position }) => {
  const map = useMap();
  useEffect(() => {
    map.flyTo(position, map.getZoom(), { animate: true, duration: 1.2 });
  }, [position, map]);
  return null;
};

const MapComponent = ({location, showMap=false, onLocationChange=()=>{}}) => {
  const [search, setSearch] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [position, setPosition] = useState(null);
  const [boundingBox, setBoundingBox] = useState(null);
  const [loading, setLoading] = useState(true);
  const [mapOpen, setMapOpen] = useState(false);
  const [readyToRenderMap, setReadyToRenderMap] = useState(false);
  const [googleReady, setGoogleReady] = useState(false);
  const mapRef = useRef(null);

  useEffect(()=>{
    if(location && Object.values(location)?.length>0){
      setSearch(location?.display);
      setPosition([location?.lat, location?.lng]);
    }
  },[location]);


  // Load Google script
  useEffect(() => {
    loadGoogleScript(() => {
      setGoogleReady(true);
    });
  }, []);

  // Get current location
  useEffect(() => {
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const coords = [pos.coords.latitude, pos.coords.longitude];
        setPosition(coords);
        reverseGeocode(coords);
        setLoading(false);
      },
      (err) => {
        console.warn('Geolocation error:', err);
        setLoading(false);
      }
    );
  }, []);

  // Ensure map renders correctly on open
  useEffect(() => {
    if (mapOpen && position) {
      setReadyToRenderMap(true);
      setTimeout(() => {
        mapRef.current?.invalidateSize();
      }, 300);
    } else {
      setReadyToRenderMap(false);
    }
  }, [mapOpen, position]);

  // Debounced autocomplete
  useEffect(() => {
    if (!googleReady || !window.google?.maps || search?.length < 3) {
      setSuggestions([]);
      return;
    }

    const delay = setTimeout(() => {
      const service = new window.google.maps.places.AutocompleteService();
      service.getPlacePredictions(
        {
          input: search,
          types: ['geocode', 'establishment'],
          componentRestrictions: { country: ['gb'] },
        },
        (predictions, status) => {
          if (status === window.google.maps.places.PlacesServiceStatus.OK && predictions) {
            setSuggestions(predictions);
          } else {
            setSuggestions([]);
          }
        }
      );
    }, 300);

    return () => clearTimeout(delay);
  }, [search, googleReady]);



  const handleSearchChange = (e) => {
    
    if(e.target.value?.trim()?.length==0){
      setSearch("");
      setSuggestions([]);
      sessionStorage.setItem("location", null);
      return;
    }else{
      setSearch(e.target.value);
    }

  };

  const handleSelect = (place) => {
    if (!googleReady || !window.google?.maps) return;

    const geocoder = new window.google.maps.Geocoder();
    geocoder.geocode({ placeId: place.place_id }, (results, status) => {
      if (status === 'OK' && results[0]) {
        const loc = results[0].geometry.location;
        const lat = loc.lat();
        const lng = loc.lng();
        setPosition([lat, lng]);
        setSearch(results[0].formatted_address);
        setSuggestions([]);
        setBoundingBox(null);

        sessionStorage.setItem("location", JSON.stringify({display: results[0].formatted_address, lat: lat, lng: lng}));
        onLocationChange()
      }
    });
  };

  const reverseGeocode = (coords) => {
    if (!googleReady || !window.google?.maps) return;

    const geocoder = new window.google.maps.Geocoder();
    geocoder.geocode({ location: { lat: coords[0], lng: coords[1] } }, (results, status) => {
      if (status === 'OK' && results[0]) {
        setSearch(results[0].formatted_address);
      }
    });
  };

  const handleDragEnd = (newPos) => {
    setPosition(newPos);
    reverseGeocode(newPos);
  };

  const getLocation = () => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          const coords = [pos.coords.latitude, pos.coords.longitude];
          setPosition(coords);
          reverseGeocode(coords);
        },
        (err) => {
          console.warn('Geolocation error:', err);
        },
        {
          enableHighAccuracy: true,
          timeout: 10000,       // wait max 10 seconds
          maximumAge: 0         // don’t use cached position
        } 
      );
    } else {
      alert("Geolocation is not supported by this browser.");
    }
  };

  return loading ? (
    <Spinner />
  ) : (
    <div>
      <Popover
        // open={mapOpen}
        placement='right'
        overlayClassName="associationCustomPopover"
        overlayStyle={{ width: '400px', backgroundColor: 'rgb(255, 122, 89)' }}
        content={
          showMap &&
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
            <X style={{
              color: 'white',
              cursor: 'pointer',
              marginTop: '-25px',
              marginRight: '-40px',
              backgroundColor: 'rgb(255, 122, 89)',
              borderRadius: '50%',
              padding: '10px'
            }} onClick={() => setMapOpen(false)} />
            {readyToRenderMap ? (
              <MapContainer
                center={position}
                zoom={30}
                style={{ height: '300px', width: '100%' }}
                whenCreated={(mapInstance) => {
                  mapRef.current = mapInstance;
                }}
              >
                <MapController position={position} />
                <TileLayer
                  attribution='&copy; OpenStreetMap contributors'
                  url='https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png'
                />
                <DraggableMarker position={position} onDragEnd={handleDragEnd} />
                {boundingBox && <Polygon positions={boundingBox} color="blue" />}
              </MapContainer>
            ) : (
              <div style={{ padding: 10 }}>Loading map...</div>
            )}
          </div>
        }
      >
        <Input
          type="text"
          value={search}
          onChange={handleSearchChange}
          placeholder="Search for address or postcode..."
          className="generic-input-control"
          style={{ width: '100%', padding: '8px', marginBottom: '8px' }}
          suffix={
            <Popover overlayStyle={{color:'black'}} content="Click to get your current location">
              <MapPinHouse onClick={getLocation} style={{ cursor: 'pointer' }} />
            </Popover>
          }
          onClick={() => setMapOpen(true)}
        />
      </Popover>

      <ul style={{ listStyle: 'none', padding: 0, marginBottom: '10px' }}>
        {suggestions.map((place) => (
          <li
            key={place.place_id}
            onClick={() => handleSelect(place)}
            className='popoverdataitem'
            style={{
              cursor: 'pointer',
              padding: '6px',
              borderBottom: '1px solid #ccc',
            }}
          >
            {place.description}
          </li>
        ))}
      </ul>
    </div>
  );
};

export default MapComponent;
